import {
  Box,
  Button,
  Center,
  Flex,
  Select,
  SimpleGrid,
  Stack,
  Text,
  TextInput,
} from '@mantine/core'
import { DateTimePicker, DatesProvider } from '@mantine/dates'
import { modals } from '@mantine/modals'
import withErrorBoundaryAndPolicyCheck from '@v2/commons/HOC/ErrorBoundaryAndPolicyCheckHOC'
import RichText from '@v2/commons/components/RichText/RichText'

import { AppointmentsByPersonRecordDto } from 'src/api/generated'
import {
  BENEFIT_TYPE_OPTIONS,
  PREFERRED_LANGUAGES_OPTION,
  STATUS_OPTIONS,
} from 'src/default/config'

import { manageAppointmentFormUseCase } from '../../hooks/manageAppointmentForm.hook'

interface ManageAppointmentModalProps {
  personId: string
  initialData?: AppointmentsByPersonRecordDto
}

const ManageAppointmentModalBase = ({
  initialData,
  personId,
}: ManageAppointmentModalProps) => {
  const { form, onSubmit, isLoading } = manageAppointmentFormUseCase(
    personId,
    initialData,
  )

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault()
        onSubmit()
      }}
    >
      <Center>
        <Text fz="h3" fw="bold">
          Manage Appointment
        </Text>
      </Center>
      <Box p="lg">
        <SimpleGrid mb="lg" cols={2} spacing="lg">
          <Select
            key={form.key('benefitType')}
            label="Benefit"
            placeholder="Select benefit"
            data={BENEFIT_TYPE_OPTIONS}
            withAsterisk
            {...form.getInputProps('benefitType')}
          />

          <Select
            key={form.key('preferredLanguage')}
            label="Preferred Language"
            placeholder="Select language"
            data={PREFERRED_LANGUAGES_OPTION}
            withAsterisk
            {...form.getInputProps('preferredLanguage')}
          />

          <TextInput
            key={form.key('clinicZipCode')}
            label="Clinic Zip Code"
            placeholder="Enter zip code"
            withAsterisk
            {...form.getInputProps('clinicZipCode')}
          />

          <DatesProvider settings={{ timezone: 'America/New_York' }}>
            <DateTimePicker
              key={form.key('appointmentDate')}
              label="Appointment Date"
              placeholder="Select date and time"
              withAsterisk
              {...form.getInputProps('appointmentDate')}
              minDate={new Date()}
              valueFormat="MM/DD/YYYY hh:mm A"
            />
          </DatesProvider>

          <Select
            key={form.key('status')}
            label="Status"
            placeholder="Select status"
            data={STATUS_OPTIONS}
            withAsterisk
            {...form.getInputProps('status')}
          />

          <TextInput
            key={form.key('repPhoneNumber')}
            label="Representative Phone Number"
            placeholder="Enter phone number"
            withAsterisk
            {...form.getInputProps('repPhoneNumber')}
            disabled
          />
        </SimpleGrid>

        <Stack>
          <RichText
            value={initialData?.clinicInformation}
            label="Clinic Information"
            placeholder="Enter clinic information"
            withAsterisk
            {...form.getInputProps('clinicInformation')}
            showAttachments={false}
            showImageAttachment={false}
          />

          <RichText
            key={form.key('recommendations')}
            label="Recomendations"
            placeholder="Enter clinic information"
            value={initialData?.recommendations}
            {...form.getInputProps('recommendations')}
            showAttachments={false}
            showImageAttachment={false}
          />
        </Stack>

        <Flex justify="space-between" align="center" mt="xl">
          <Button variant="outline" onClick={() => modals.closeAll()}>
            Cancel
          </Button>
          <Button type="submit" loading={isLoading}>
            {initialData?.id ? 'Update' : 'Create'} Appointment
          </Button>
        </Flex>
      </Box>
    </form>
  )
}

export const ManageAppointmentModalContent = withErrorBoundaryAndPolicyCheck(
  ManageAppointmentModalBase,
  'is_create_appointment_enabled',
)

export const openManageAppointmentModal = (
  personId: string,
  initialData?: AppointmentsByPersonRecordDto,
) =>
  modals.open({
    children: (
      <ManageAppointmentModalContent
        initialData={initialData}
        personId={personId}
      />
    ),
    size: 'xl',
    centered: true,
  })
